/**
 * RunnerListV2 Component
 * Desktop: Table with sortable column headers (Pencil `xfhxL`)
 * Mobile: Card list (Pencil `DP4ps`)
 */

import { useState, useMemo } from 'react';
import type { AidStation, RunnerSummary } from '../../lib/types';
import { RunnerRowDesktop, RunnerRowMobile } from './RunnerRowV2';
import { SmsSubscribeButtonV2 } from './SmsSubscribeButtonV2';
import { isHiddenParentStation } from '../../lib/hiddenParentStation';

export type V2SortKey = 'rank' | 'runner' | 'pace' | 'last_cp' | 'next_cp' | 'status';
type SortDir = 'asc' | 'desc';

interface RunnerListV2Props {
  runners: RunnerSummary[];
  stations?: AidStation[];
  onRowClick: (runnerId: string) => void;
  raceTimeZone?: string;
  followedBibs: Set<string>;
  onToggleFollow: (bib: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  raceSlug?: string;
}


export function RunnerListV2({
  runners,
  stations,
  onRowClick,
  raceTimeZone,
  followedBibs,
  onToggleFollow,
  search,
  onSearchChange,
  raceSlug,
}: RunnerListV2Props) {
  const tz = raceTimeZone || 'America/Los_Angeles';

  // Build station-name → mileage map and total race distance for pace calculation.
  // Uses the finish station's mileage as the total when available, otherwise the max
  // station mileage. Parent multi-visit stations are skipped because runners only
  // check in at virtual children.
  const { stationMileage, totalDistanceMiles } = useMemo(() => {
    const map = new Map<string, number>();
    let total = 0;
    let finish = 0;
    for (const s of stations ?? []) {
      if (isHiddenParentStation(s)) continue;
      map.set(s.name, s.mileage);
      if (s.mileage > total) total = s.mileage;
      if (s.check_mode === 'finish' && s.mileage > finish) finish = s.mileage;
    }
    return { stationMileage: map, totalDistanceMiles: finish || total || undefined };
  }, [stations]);
  const [sortKey, setSortKey] = useState<V2SortKey>('rank');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: V2SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRunners = useMemo(() => {
    const sorted = [...runners];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'rank':
          cmp = (a.overall_place ?? 9999) - (b.overall_place ?? 9999);
          break;
        case 'runner':
          cmp = `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
          break;
        case 'last_cp':
          cmp = (a.last_checkin_station_position ?? -1) - (b.last_checkin_station_position ?? -1);
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        default:
          cmp = (a.overall_place ?? 9999) - (b.overall_place ?? 9999);
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [runners, sortKey, sortDir]);

  const activeRunners = sortedRunners.filter(r => !r.is_dropped);
  const droppedRunners = sortedRunners.filter(r => r.is_dropped);
  const displayRunners = [...activeRunners, ...droppedRunners];

  const sortIndicator = (key: V2SortKey) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <>
      {/* Search / filter bar */}
      <div className="v2-search-section">
        <div className="v2-search-left">
          <span className="v2-section-title">LIVE RACE</span>
          <span className="v2-runner-count">{runners.length} runners</span>
        </div>
        <div className="v2-search-wrapper">
          <svg className="v2-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="v2-search-input"
            placeholder="Search by name or bib..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      {raceSlug && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 16px 16px', background: 'var(--v2-bg-card)' }}>
          <SmsSubscribeButtonV2 slug={raceSlug} />
        </div>
      )}

      {/* Desktop table */}
      <div className="v2-runner-table-desktop">
        <table className="v2-runner-table">
          <thead>
            <tr>
              <th className={sortKey === 'rank' ? 'active' : ''} onClick={() => handleSort('rank')} style={{ width: 64 }}>
                RANK{sortIndicator('rank')}
              </th>
              <th className={sortKey === 'runner' ? 'active' : ''} onClick={() => handleSort('runner')}>
                RUNNER{sortIndicator('runner')}
              </th>
              <th onClick={() => handleSort('pace')} style={{ width: 80 }}>
                PACE{sortIndicator('pace')}
              </th>
              <th className={sortKey === 'last_cp' ? 'active' : ''} onClick={() => handleSort('last_cp')} style={{ width: 200 }}>
                LAST CHECKPOINT{sortIndicator('last_cp')}
              </th>
              <th style={{ width: 130 }}>NEXT CHECKPOINT</th>
              <th className={sortKey === 'status' ? 'active' : ''} onClick={() => handleSort('status')} style={{ width: 100 }}>
                STATUS{sortIndicator('status')}
              </th>
              <th style={{ width: 48 }}></th>
            </tr>
          </thead>
          <tbody>
            {displayRunners.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--v2-text-muted)' }}>
                  No runners match your search.
                </td>
              </tr>
            ) : (
              displayRunners.map((runner) => (
                <RunnerRowDesktop
                  key={runner.id}
                  runner={runner}
                  onClick={onRowClick}
                  raceTimeZone={tz}
                  isFollowed={followedBibs.has(runner.bib)}
                  onToggleFollow={onToggleFollow}
                  nextStation={runner.next_checkpoint ?? undefined}
                  stationMileage={stationMileage}
                  totalDistanceMiles={totalDistanceMiles}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="v2-runner-mobile-list">
        {displayRunners.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--v2-text-muted)', background: 'var(--v2-bg-card)' }}>
            No runners match your search.
          </div>
        ) : (
          displayRunners.map((runner) => (
            <RunnerRowMobile
              key={runner.id}
              runner={runner}
              onClick={onRowClick}
              raceTimeZone={tz}
              isFollowed={followedBibs.has(runner.bib)}
              onToggleFollow={onToggleFollow}
              nextStation={runner.next_checkpoint ?? undefined}
            />
          ))
        )}
      </div>
    </>
  );
}
